import { useState, useCallback, useRef } from 'react';

/**
 * Hook to manage strategem targeting animations
 * Tracks active animations, prevents duplicates, and manages ghost units
 * Ghost units keep targeted units visible until the animation completes
 */
export const useStrategemTargetAnimations = () => {
  const [activeAnimations, setActiveAnimations] = useState(new Map());
  const completedAnimationsRef = useRef(new Set());
  const animatedInstanceIdsRef = useRef(new Set()); // Track all animated strategem instanceIds
  const targetedUnitInstanceIdsRef = useRef(new Set()); // Track unit instanceIds being targeted by strategems
  const ghostUnitsRef = useRef(new Map()); // Track ghost units: slotKey -> { unit, animationKey }

  /**
   * Register a new strategem targeting animation
   * @param {Object} animationData - { fromHand, toSlot, targetType, strategemId, strategemInstanceId, animationKey, ghostUnit }
   * @param {Object} animationData.ghostUnit - Optional: Full unit data to keep visible as a "ghost" during animation
   */
  const registerAnimation = useCallback((animationData) => {
    if (!animationData || !animationData.fromHand || !animationData.toSlot) {
      console.warn('⚠️ useStrategemTargetAnimations: Invalid animation data', animationData);
      return;
    }

    // Create unique animation key
    const animationKey = animationData.animationKey || 
      `${animationData.strategemInstanceId || 'unknown'}-${animationData.targetType}-${animationData.toSlot.zone}-${animationData.toSlot.index !== null ? animationData.toSlot.index : 'general'}-${animationData.toSlot.playerId || 'unknown'}`;

    // Use instanceId-based key to prevent duplicates
    const instanceIdKey = animationData.strategemInstanceId 
      ? `instance-${animationData.strategemInstanceId}-${animationData.targetType}-${animationData.toSlot.zone}-${animationData.toSlot.index !== null ? animationData.toSlot.index : 'general'}-${animationData.toSlot.playerId || 'unknown'}`
      : null;

    // ✅ CRITICAL: Track refs SYNCHRONOUSLY before the async state update
    // This ensures death detection can see the targets immediately in the same render cycle
    if (animationData.strategemInstanceId) {
      animatedInstanceIdsRef.current.add(animationData.strategemInstanceId);
      // Clean up after 5 seconds (plenty of time for game state to sync)
      setTimeout(() => {
        animatedInstanceIdsRef.current.delete(animationData.strategemInstanceId);
      }, 5000);
    }

    // Track the target unit's instanceId for death animation suppression - SYNCHRONOUSLY
    if (animationData.targetInstanceId) {
      targetedUnitInstanceIdsRef.current.add(animationData.targetInstanceId);
      console.log(`🎬 SYNC: Tracking targetInstanceId for death suppression: ${animationData.targetInstanceId}`);
      // Clean up after 5 seconds
      setTimeout(() => {
        targetedUnitInstanceIdsRef.current.delete(animationData.targetInstanceId);
      }, 5000);
    }

    // ✅ GHOST UNITS: Store ghost unit data SYNCHRONOUSLY so getVisibleUnit can see it immediately
    // Skip if isOpponentStrategem - those ghosts are stored by useMemo in useOpponentStrategemAnimations
    // Skip if ghost already exists for this slot (prevents duplicate registrations)
    if (animationData.ghostUnit && animationData.toSlot && !animationData.isOpponentStrategem) {
      const zone = animationData.toSlot.zone;
      const index = animationData.toSlot.index;
      if (zone && index !== null && index !== undefined) {
        const slotKey = `${zone}_${index}`;
        // Only store if not already present (prevent duplicates)
        if (!ghostUnitsRef.current.has(slotKey)) {
          ghostUnitsRef.current.set(slotKey, {
            unit: animationData.ghostUnit,
            animationKey: animationKey,
            targetInstanceId: animationData.targetInstanceId
          });
          // Ghost stored for visual persistence during animation
        }
      }
    }

    setActiveAnimations(prev => {
      // Prevent duplicates
      if (prev.has(animationKey) || completedAnimationsRef.current.has(animationKey)) {
        console.log('⚠️ useStrategemTargetAnimations: Animation already active or completed (by key)', animationKey);
        return prev;
      }
      
      if (instanceIdKey) {
        const existingByInstanceId = Array.from(prev.values()).find(anim => {
          const animInstanceKey = anim.strategemInstanceId 
            ? `instance-${anim.strategemInstanceId}-${anim.targetType}-${anim.toSlot.zone}-${anim.toSlot.index !== null ? anim.toSlot.index : 'general'}-${anim.toSlot.playerId || 'unknown'}`
            : null;
          return animInstanceKey === instanceIdKey;
        });
        
        if (existingByInstanceId) {
          console.log('⚠️ useStrategemTargetAnimations: Animation already active for this strategem instance', instanceIdKey);
          return prev;
        }
        
        if (completedAnimationsRef.current.has(instanceIdKey)) {
          console.log('⚠️ useStrategemTargetAnimations: Animation already completed for this strategem instance', instanceIdKey);
          return prev;
        }
      }

      console.log(`✅ useStrategemTargetAnimations: Registering animation`, animationKey, { 
        instanceId: animationData.strategemInstanceId, 
        instanceIdKey,
        fromHand: animationData.fromHand,
        toSlot: animationData.toSlot,
        targetInstanceId: animationData.targetInstanceId,
        hasGhost: !!animationData.ghostUnit,
        effectType: animationData.effectType // Log effectType to verify it's being passed
      });

      const next = new Map(prev);
      next.set(animationKey, {
        ...animationData,
        key: animationKey,
        instanceIdKey,
        registeredAt: Date.now()
      });
      console.log(`✅ useStrategemTargetAnimations: Active animations count: ${next.size}`);
      return next;
    });
  }, []);

  /**
   * Complete an animation (remove from active and clean up ghost)
   * @param {string} animationKey - Unique animation key
   */
  const completeAnimation = useCallback((animationKey) => {
    console.log(`✅ useStrategemTargetAnimations: Completing animation`, animationKey);
    
    setActiveAnimations(prev => {
      const animation = prev.get(animationKey);
      const strategemInstanceId = animation?.strategemInstanceId;
      
      // ✅ GHOST CLEANUP: Remove ghost units immediately when animation completes
      // The damage animation starts at the same time, so no delay needed
      for (const [slotKey, ghostData] of ghostUnitsRef.current.entries()) {
        // Match by animationKey (frontend-captured ghosts)
        if (ghostData.animationKey === animationKey) {
          ghostUnitsRef.current.delete(slotKey);
          console.log(`🧹 Cleaned up ghost unit for animationKey: ${animationKey}`);
          
          // ✅ BUG FIX: Don't remove targetInstanceId immediately - keep it for death animation suppression
          // The 5 second timeout in registerAnimation will handle cleanup
          // This prevents duplicate death animations when the unit dies after the strategem animation completes
          // Similar to how deadTargetsRef works for attacks - keep tracking until slot death detection runs
        }
        // ✅ CRITICAL FIX: Also clean up backend ghost units for this strategem instanceId
        // Backend ghost units use keys like "backend-{strategemInstanceId}-{targetIndex}"
        // When any animation for this strategem completes, clean up all backend ghosts for it
        else if (strategemInstanceId && ghostData.animationKey?.startsWith(`backend-${strategemInstanceId}-`)) {
          ghostUnitsRef.current.delete(slotKey);
          console.log(`🧹 Cleaned up backend ghost unit for strategem instanceId: ${strategemInstanceId}, slot: ${slotKey}`);
          
          // ✅ BUG FIX: Don't remove targetInstanceId immediately - keep it for death animation suppression
          // The 5 second timeout in registerAnimation will handle cleanup
          // This prevents duplicate death animations when the unit dies after the strategem animation completes
        }
      }
      
      const next = new Map(prev);
      next.delete(animationKey);
      
      // Mark as completed by instanceId
      if (animation && animation.instanceIdKey) {
        completedAnimationsRef.current.add(animation.instanceIdKey);
        console.log(`✅ useStrategemTargetAnimations: Marked instanceId animation as completed`, animation.instanceIdKey);
      }
      
      // Also mark by animationKey - keep longer to prevent duplicate damage animations
      completedAnimationsRef.current.add(animationKey);
      setTimeout(() => {
        completedAnimationsRef.current.delete(animationKey);
        if (animation?.instanceIdKey) {
          completedAnimationsRef.current.delete(animation.instanceIdKey);
        }
      }, 2000); // 2 seconds to handle game state sync delays
      
      return next;
    });
  }, []);

  /**
   * Get ghost unit for a slot (if any)
   * Used by getVisibleUnit to show ghost while animation is in progress
   * Checks BOTH the ghostUnitsRef AND active animations for ghost data
   * @param {string} zone - Zone name (e.g., "your_front", "opponent_flank")
   * @param {number} index - Slot index
   * @returns {Object|null} - Ghost unit data or null
   */
  const getGhostUnit = useCallback((zone, index) => {
    const slotKey = `${zone}_${index}`;
    
    // First check the ref (for ghosts registered by previous effects)
    const ghostData = ghostUnitsRef.current.get(slotKey);
    if (ghostData) {
      // Return ghost unit for visual persistence
      return ghostData.unit;
    }
    
    // Also check active animations (for ghosts in current render cycle)
    for (const anim of activeAnimations.values()) {
      if (anim.ghostUnit && anim.toSlot) {
        const animSlotKey = `${anim.toSlot.zone}_${anim.toSlot.index}`;
        if (animSlotKey === slotKey) {
          // Return ghost from active animation
          return anim.ghostUnit;
        }
      }
    }
    
    // No logging for NOT FOUND - too noisy
    return null;
  }, [activeAnimations]);

  /**
   * Check if a strategem animation is targeting the general
   * Used to suppress duplicate damage animations from useStrategemGeneralDamage
   * @param {string} strategemInstanceId - The strategem's instanceId
   * @returns {boolean} - True if there's an active/recent animation for this strategem targeting general
   */
  const isAnimatingToGeneral = useCallback((strategemInstanceId) => {
    if (!strategemInstanceId) return false;
    
    // Check active animations
    for (const anim of activeAnimations.values()) {
      if (anim.strategemInstanceId === strategemInstanceId && 
          (anim.targetType === 'general' || anim.toSlot?.zone === 'general')) {
        return true;
      }
    }
    
    // Check recently completed animations (within last 500ms)
    const recentKey = `instance-${strategemInstanceId}-general`;
    return completedAnimationsRef.current.has(recentKey);
  }, [activeAnimations]);

  /**
   * Get all strategem instanceIds that have active/recent animations
   * Used by useStrategemGeneralDamage to skip duplicate animations
   * Returns the ref's Set directly for real-time access
   */
  const getAnimatedStrategemInstanceIds = useCallback(() => {
    return animatedInstanceIdsRef.current;
  }, []);

  return {
    activeAnimations: Array.from(activeAnimations.values()),
    registerAnimation,
    completeAnimation,
    isAnimatingToGeneral,
    getAnimatedStrategemInstanceIds,
    getGhostUnit, // Get ghost unit for a slot
    ghostUnitsRef, // Expose ref for synchronous ghost storage by useOpponentStrategemAnimations
    targetedUnitInstanceIdsRef // Expose for death animation suppression
  };
};
